import {KebabHorizontalIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton} from '@primer/react'
import type {RefObject} from 'react'
import {useTaskActionItems, type TaskActionItem, type UseTaskActionItemsProps} from '../hooks/use-task-action-items'

export interface TaskActionsProps extends UseTaskActionItemsProps {
  /** aria-label for the kebab button (default: "Task actions") */
  ariaLabel?: string
  /** Alignment of the overlay (default: undefined / start) */
  overlayAlign?: 'start' | 'end'
  /** Ref for the ActionMenu anchor button */
  anchorRef?: RefObject<HTMLButtonElement | null>
  /** data-testid for the kebab button */
  testId?: string
  /** Called when the kebab button is clicked (e.g. for analytics) */
  onOpen?: () => void
  /** Additional items to append to the action menu */
  extraItems?: TaskActionItem[]
}

/**
 * Kebab ActionMenu for task actions. Renders the full menu: anchor button + overlay + action list.
 * Returns null when no actions are available.
 */
export function TaskActions({
  ariaLabel = 'Task actions',
  overlayAlign,
  anchorRef,
  testId,
  onOpen,
  extraItems,
  ...actionProps
}: TaskActionsProps) {
  const {items, hasAnyAction} = useTaskActionItems(actionProps)
  const allItems = extraItems?.length ? [...items, ...extraItems] : items

  if (!hasAnyAction && !extraItems?.length) return null

  return (
    <ActionMenu anchorRef={anchorRef}>
      <ActionMenu.Anchor>
        <IconButton
          icon={KebabHorizontalIcon}
          variant="invisible"
          aria-label={ariaLabel}
          onClick={onOpen}
          {...(testId ? {'data-testid': testId} : {})}
        />
      </ActionMenu.Anchor>
      <ActionMenu.Overlay align={overlayAlign}>
        <ActionList>
          {allItems.map(item =>
            item.href ? (
              <ActionList.LinkItem key={item.key} href={item.href} onClick={() => item.onSelect()}>
                <ActionList.LeadingVisual>
                  <item.icon />
                </ActionList.LeadingVisual>
                {item.label}
              </ActionList.LinkItem>
            ) : (
              <ActionList.Item key={item.key} variant={item.variant} onSelect={item.onSelect}>
                <ActionList.LeadingVisual>
                  <item.icon />
                </ActionList.LeadingVisual>
                {item.label}
              </ActionList.Item>
            ),
          )}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
